import * as React from 'react';
import { differenceInHours, parseISO } from 'date-fns';
import { useRouter } from 'next/router';
import useModal from '@/hooks/useModal';
import { NEW_ACCOUNT_WINDOW_HOURS } from '@/const/actionRequired';
import {
	UseActionRequiredOnboardingParams,
	UseActionRequiredOnboardingResult,
} from '@/interfaces';
import { setLocalStorage } from '@/utils/localStorage';

export const useActionRequiredOnboarding = ({
	isDashboard,
	actions,
}: UseActionRequiredOnboardingParams): UseActionRequiredOnboardingResult => {
	const router = useRouter();
	const {
		userMe,
		caseId,
		totalCases,
		isIdentityVerificationOpen,
		isStandaloneVerificationOpen,
		isModalFilesOpen,
		isModalFileInstructions,
		startIdentityVerification,
	} = actions;

	const {
		Modal: WelcomeModal,
		show: showWelcome,
		hide: hideWelcome,
	} = useModal();

	const hasDismissedWelcomeRef = React.useRef(false);
	const dismissWelcome = (): void => {
		hasDismissedWelcomeRef.current = true;
		hideWelcome();
	};

	const showTakeTheTourAction =
		!!isDashboard &&
		!!userMe?.data &&
		!userMe.data.is_verified &&
		userMe.data.identity_verification_passed !== true;
	const {
		Modal: ModalGuidedTour,
		show: showGuidedTour,
		hide: hideGuidedTour,
	} = useModal();
	const handleTakeTheTourClick = (): void => {
		if (userMe?.data?.id) {
			setLocalStorage(`guided_tour_seen_${userMe.data.id}`, true);
		}
		showGuidedTour();
	};

	const isWithinNewAccountWindow =
		!!userMe?.data?.created_at &&
		Math.abs(differenceInHours(new Date(), parseISO(userMe.data.created_at))) <
			NEW_ACCOUNT_WINDOW_HOURS;

	React.useEffect(() => {
		if (
			!hasDismissedWelcomeRef.current &&
			!!isDashboard &&
			userMe?.data?.is_verified &&
			userMe?.data?.identity_verification_passed !== true &&
			!!caseId &&
			!router.query.evaluated &&
			!isIdentityVerificationOpen &&
			!isStandaloneVerificationOpen &&
			!isModalFilesOpen &&
			!isModalFileInstructions &&
			totalCases === 1 &&
			isWithinNewAccountWindow
		) {
			const timeout = setTimeout(showWelcome, 600);
			return () => clearTimeout(timeout);
		}
		hideWelcome();
	}, [
		isDashboard,
		userMe,
		caseId,
		router.query.evaluated,
		isIdentityVerificationOpen,
		isStandaloneVerificationOpen,
		isModalFilesOpen,
		isModalFileInstructions,
		totalCases,
		isWithinNewAccountWindow,
	]);

	const handleStartVerificationFromWelcome = (): void => {
		dismissWelcome();
		startIdentityVerification();
	};

	return {
		showTakeTheTourAction,
		handleTakeTheTourClick,
		WelcomeModal,
		dismissWelcome,
		handleStartVerificationFromWelcome,
		ModalGuidedTour,
		hideGuidedTour,
	};
};
